import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, FileText, Info } from 'lucide-react';

// Avviso in cima al wizard (2026-11-01): il verbale emesso non corrisponde più ai dati di oggi, e
// cosa è cambiato. Lo vedono gli admin che possono riscriverlo. Le revisioni precedenti restano in archivio.

export interface SourceChange { area: string; label: string; added?: string[]; removed?: string[]; changed?: string[] }
interface Status { issued: boolean; canManage: boolean; tracked: boolean; outdated: boolean; revision?: number; issuedAt?: string; hasDraft?: boolean; changes: SourceChange[] }

export function changeText(c: SourceChange): string {
  const parts = [c.added?.length ? `added ${c.added.join(', ')}` : null, c.removed?.length ? `removed ${c.removed.join(', ')}` : null, c.changed?.length ? `changed ${c.changed.join(', ')}` : null].filter(Boolean);
  return parts.length ? `${c.label}: ${parts.join('; ')}` : c.label;
}

export default function OnboardingDocumentStatus({ tenantId, refreshKey, onOpen, isOnSummary }: { tenantId: number; refreshKey: number; onOpen: () => void; isOnSummary: boolean }) {
  const [status, setStatus] = useState<Status | null>(null);

  useEffect(() => {
    let current = true;
    axios.get(`/api/onboarding/${tenantId}/document/status`).then(res => { if (current) setStatus(res.data.data); }).catch(() => { if (current) setStatus(null); });
    return () => { current = false; };
  }, [tenantId, refreshKey]);

  if (!status?.issued || !status.canManage || (status.tracked && !status.outdated)) return null;
  const date = status.issuedAt ? new Date(status.issuedAt).toLocaleDateString('en-GB') : '';
  const open = !isOnSummary && <button onClick={onOpen} className="btn-secondary text-sm inline-flex items-center gap-1 shrink-0"><FileText size={13} /> Open the document</button>;

  if (!status.tracked) {
    return (
      <div className="card p-3 text-sm text-gray-600 flex flex-wrap items-center gap-3">
        <Info size={15} className="text-primary-600 shrink-0" />
        <span className="flex-1 min-w-[240px]">Rev.{status.revision} of the onboarding document ({date}) was issued before changes were tracked, so it cannot be compared with today's data. Issue a new revision to start tracking.</span>
        {open}
      </div>
    );
  }
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex flex-wrap items-start gap-3" role="status">
      <AlertTriangle size={16} className="text-amber-700 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-[240px] text-sm text-amber-900">
        <p className="font-medium">The onboarding document is out of date: regenerate it.</p>
        <p className="text-xs mt-0.5">Rev.{status.revision} ({date}) no longer matches the customer's data. It stays in the archive when you issue a new revision.{status.hasDraft ? ' A draft is already in progress.' : ''}</p>
        <ul className="text-xs mt-1 list-disc ml-4 space-y-0.5">
          {status.changes.map(c => <li key={c.area}>{changeText(c)}</li>)}
        </ul>
      </div>
      {open}
    </div>
  );
}
